// frontend/src/components/PostProduction/SummaryMetrics.tsx
import React from 'react';
import { Box, Card, CardContent, Typography, Grid, Tooltip, IconButton } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DashboardSummary } from '../../types/PPDash';

interface SummaryMetricsProps {
  summary: DashboardSummary;
}

const SummaryMetrics: React.FC<SummaryMetricsProps> = ({ summary }) => {
  const metrics = [
    {
      label: 'Total Shows',
      value: summary.totalShows,
      format: (v: number | string) => (typeof v === 'number' ? v.toString() : v.toString()),
      explainer: 'Total number of performances in the selected date range with post-production reports.'
    },
    {
      label: 'Total Audience',
      value: summary.totalAudience,
      format: (v: number | string) => (typeof v === 'number' ? v.toLocaleString() : v),
      explainer: 'Combined attendance across all shows (tickets sold and scanned at door).'
    },
    {
      label: 'Avg Capacity',
      value: summary.averageCapacity,
      format: (v: number | string) => `${typeof v === 'number' ? v.toFixed(1) : v}%`,
      explainer: 'Average percentage of venue capacity filled. Based on total attendance vs venue capacity.'
    },
    {
      label: 'Avg No-Show Rate',
      value: summary.averageNoShowRate,
      format: (v: number | string) => `${typeof v === 'number' ? v.toFixed(1) : v}%`,
      explainer: 'Average percentage of ticket holders who bought tickets but didn\'t attend. Calculated as (tickets sold - tickets scanned) ÷ tickets sold.'
    },
    {
      label: 'Avg Subsidy',
      value: summary.averageSubsidy,
      format: (v: number | string) => `€${typeof v === 'number' ? v.toFixed(0) : v}`,
      explainer: 'Average subsidy per show. Calculated as (total costs - total revenue). Negative values indicate profit.'
    },
    {
      label: 'Avg Subsidy/Ticket',
      value: summary.averageSubsidyPerTicket,
      format: (v: number | string) => `€${typeof v === 'number' ? v.toFixed(2) : v}`,
      explainer: 'Average subsidy per audience member. Total subsidy divided by total attendance. Shows the per-seat cost of programming.'
    },
    {
      label: 'Sell-Outs (95%+)',
      value: summary.sellOutCount,
      format: (v: number | string) => (typeof v === 'number' ? v.toString() : v.toString()),
      explainer: 'Number of shows that reached 95%+ capacity. We typically stop sales at 95% to allow for walk-ups at our 50-capacity venue.'
    },
    {
      label: 'Most Profitable Genre',
      value: summary.mostProfitableGenre || 'N/A',
      format: (v: number | string) => v.toString(),
      explainer: 'Jazz sub-genre with the lowest average subsidy (or highest profit). Based on genre data from Programming records.'
    }
  ];

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        Summary Metrics
      </Typography>
      <Grid container spacing={3}>
        {metrics.map((metric, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ 
              height: '100%',
              backgroundColor: 'background.paper'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                  >
                    {metric.label}
                  </Typography>
                  <Tooltip title={metric.explainer} arrow placement="top">
                    <IconButton size="small" sx={{ p: 0 }}>
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography 
                  variant="h4" 
                  component="div"
                  sx={{ fontWeight: 600, mb: 1 }}
                >
                  {metric.format(metric.value)}
                </Typography>
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ 
                    display: 'block',
                    minHeight: '32px',
                    lineHeight: 1.3
                  }}
                >
                  {metric.explainer}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default SummaryMetrics;